import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { X, ChevronLeft, Printer, Save } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/utils/errorMessage';
import {
	useGetMedicalRecordByPatient,
	useGetMedicalRecordSession,
	useGetMedicalRecordSessions,
	useGetPatient,
	useGetSessions,
} from '@/services';
import { buildProntuarioPatientContext } from '@/pages/professional/sessao-chamada/utils/mapMedicalRecordToProntuario';
import {
	buildPlatformSessionByRecordId,
	buildProntuarioSortedSessionsList,
} from '@/pages/professional/sessao-chamada/utils/buildProntuarioSortedSessionsList';
import { resolveProntuarioPatientHeader } from '@/pages/professional/sessao-chamada/utils/resolveProntuarioPatientHeader';
import { resolveSessionParticipantTarget } from '@/pages/professional/sessao-chamada/utils/prontuarioPatientSummary';
import { formatProntuarioSessionDate } from '@/pages/professional/sessao-chamada/utils/formatProntuarioSessionDate';
import { mergeMedicalRecordSessionIntoList } from '@/utils/medicalRecordSessionPostProcessing';
import { SessionCallProntuarioPatientDetailView } from '@/pages/professional/sessao-chamada/components/prontuario/SessionCallProntuarioPatientDetailView';
import { SessionCallProntuarioPatientProfileHero } from '@/pages/professional/sessao-chamada/components/prontuario/SessionCallProntuarioPatientProfileHero';
import { resolveProntuarioProfileHeroMeta } from '@/pages/professional/sessao-chamada/utils/resolveProntuarioProfileHeroMeta';
import { SessionCallProntuarioHistoricoTab } from '@/pages/professional/sessao-chamada/components/SessionCallProntuarioHistoricoTab';
import { SessionCallProntuarioPlanoTerapeuticoTab } from '@/pages/professional/sessao-chamada/components/SessionCallProntuarioPlanoTerapeuticoTab';
import { ProntuarioProfileAccordionBlock } from '@/pages/professional/sessao-chamada/components/prontuario/ProntuarioProfileAccordionBlock';
import { ProntuarioSessionsList } from '@/pages/professional/sessao-chamada/components/prontuario/ProntuarioSessionCard';
import {
	ProntuarioEditableSessionDetailView,
	type ProntuarioSessionEditorState,
} from './ProntuarioEditableSessionDetailView';
import { ProntuarioUnsavedChangesModal } from './ProntuarioUnsavedChangesModal';
import { printProntuarioSession } from '../utils/prontuarioSessionPrint';
import {
	PATIENT_EDUCATION_LEVEL_LABELS,
	PATIENT_MARITAL_STATUS_LABELS,
} from '@/constants/patient';
import { formatDateDisplay } from '@/utils/participantDetailDisplay';

type ProntuarioExitIntent = 'close-modal' | 'back-to-sessions';

type ProntuarioModalView = 'sessions-list' | 'session-detail';

type LeftPanelSection = 'data' | 'clinical-history' | 'therapeutic-plan';

const LEFT_PANEL_CONTENT_MAX_HEIGHT_CLASS = 'max-h-[calc(88vh-27rem)]';

const PRONTUARIO_MODAL_SCROLL_CLASS = cn(
	'overflow-y-auto overscroll-contain scroll-smooth [scrollbar-gutter:stable]',
	'[scrollbar-width:thin] [scrollbar-color:theme(colors.zinc.300)_transparent]',
	'[&::-webkit-scrollbar]:w-1.5',
	'[&::-webkit-scrollbar-track]:bg-transparent',
	'[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-zinc-300',
	'hover:[&::-webkit-scrollbar-thumb]:bg-zinc-400',
);

type Props = {
	isOpen: boolean;
	onClose: () => void;
	session?: Sessions.Session | null;
	patientId?: string;
	initialMedicalRecordSessionId?: string | null;
	initialView: 'session-detail' | 'sessions-list';
};

const MEDICAL_RECORD_SESSIONS_PAGE_SIZE = 20;

const resolvePatientSessionsTitleSuffix = (
	fullName?: string | null,
): string => {
	const trimmed = fullName?.trim();
	if (!trimmed) return '';
	return trimmed.split(/[.!?]/)[0]?.trim() ?? trimmed;
};

const RIGHT_PANEL_SLIDE_TRANSITION_CLASS =
	'transition-transform duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none';

const RIGHT_PANEL_HEADER_CLASS =
	'flex h-[4.375rem] shrink-0 border-b border-zinc-200 bg-white px-5';

type SessionsListHeaderProps = {
	patientFullName?: string | null;
};

const SessionsListHeader = ({ patientFullName }: SessionsListHeaderProps) => {
	const patientTitleSuffix =
		resolvePatientSessionsTitleSuffix(patientFullName);

	return (
		<div
			className={cn(
				RIGHT_PANEL_HEADER_CLASS,
				'min-w-0 flex-col justify-center',
			)}
		>
			<p className="font-albert truncate text-[17px] font-bold leading-tight text-zinc-950">
				Sessões{patientTitleSuffix ? ` · ${patientTitleSuffix}` : ''}
			</p>
			<p className="mt-1 text-[13px] text-zinc-400">
				Selecione uma sessão para revisar as anotações.
			</p>
		</div>
	);
};

// ── Main modal ────────────────────────────────────────────────────────────────

const ProfessionalProntuarioModal = ({
	isOpen,
	onClose,
	session = null,
	patientId: patientIdProp,
	initialMedicalRecordSessionId = null,
	initialView,
}: Props) => {
	const queryClient = useQueryClient();
	const [activeLeftSection, setActiveLeftSection] =
		useState<LeftPanelSection | null>(null);
	const [rightView, setRightView] =
		useState<ProntuarioModalView>(initialView);
	const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
		initialView === 'session-detail'
			? initialMedicalRecordSessionId ??
					session?.medical_record_session?.id ??
					null
			: null,
	);
	const [sessionsPage, setSessionsPage] = useState(1);
	const [accumulatedSessions, setAccumulatedSessions] = useState<
		MedicalRecords.MedicalRecordSession[]
	>([]);
	const [sessionEditorState, setSessionEditorState] =
		useState<ProntuarioSessionEditorState | null>(null);
	const [sessionEditorRemountKey, setSessionEditorRemountKey] = useState(0);
	const [pendingExit, setPendingExit] = useState<ProntuarioExitIntent | null>(
		null,
	);
	const [isExitSaving, setIsExitSaving] = useState(false);
	const [isPrintSaving, setIsPrintSaving] = useState(false);

	const hasUnsavedSessionChanges =
		rightView === 'session-detail' && !!sessionEditorState?.hasChanges;

	const completeExit = useCallback(
		(intent: ProntuarioExitIntent, discardChanges = false) => {
			if (discardChanges) {
				setSessionEditorRemountKey((key) => key + 1);
			}

			setPendingExit(null);

			if (intent === 'close-modal') {
				onClose();
				return;
			}

			setRightView('sessions-list');
		},
		[onClose],
	);

	const requestExit = useCallback(
		(intent: ProntuarioExitIntent) => {
			if (hasUnsavedSessionChanges) {
				setPendingExit(intent);
				return;
			}

			completeExit(intent);
		},
		[completeExit, hasUnsavedSessionChanges],
	);

	const handleSaveAndExit = useCallback(async () => {
		if (!sessionEditorState || !pendingExit) return;

		setIsExitSaving(true);
		try {
			await sessionEditorState.saveAsync();
			toast.success('Sessão atualizada com sucesso.');
			completeExit(pendingExit);
		} catch (error) {
			toast.error(
				getErrorMessage(
					error,
					'Erro ao salvar a sessão. Tente novamente.',
				),
			);
		} finally {
			setIsExitSaving(false);
		}
	}, [completeExit, pendingExit, sessionEditorState]);

	const handleExitWithoutSaving = useCallback(() => {
		if (!pendingExit) return;
		completeExit(pendingExit, true);
	}, [completeExit, pendingExit]);

	const handleSessionEditorStateChange = useCallback(
		(state: ProntuarioSessionEditorState | null) => {
			setSessionEditorState(state);
		},
		[],
	);

	useEffect(() => {
		if (rightView !== 'session-detail') {
			setSessionEditorState(null);
		}
	}, [rightView]);

	useEffect(() => {
		if (!isOpen) return;
		setRightView(initialView);
		setSelectedSessionId(
			initialView === 'session-detail'
				? initialMedicalRecordSessionId ??
						session?.medical_record_session?.id ??
						null
				: null,
		);
		setSessionsPage(1);
		setAccumulatedSessions([]);
		setActiveLeftSection(null);
		setPendingExit(null);
		setIsExitSaving(false);
		setSessionEditorRemountKey(0);
	}, [
		initialMedicalRecordSessionId,
		isOpen,
		initialView,
		session?.medical_record_session?.id,
	]);

	const resolvedPatientId = useMemo(() => {
		if (patientIdProp) return patientIdProp;
		return resolveSessionParticipantTarget(session).patientId;
	}, [patientIdProp, session]);

	const {
		data: patient,
		isLoading: isPatientLoading,
		isError: isPatientError,
	} = useGetPatient(resolvedPatientId ?? '', {
		enabled: isOpen && !!resolvedPatientId,
	});

	const { data: medicalRecord, isLoading: isMedicalRecordLoading } =
		useGetMedicalRecordByPatient(resolvedPatientId ?? '', {
			enabled: isOpen && !!resolvedPatientId,
		});

	useEffect(() => {
		if (!isOpen || !resolvedPatientId) return;

		void queryClient.invalidateQueries({
			queryKey: ['sessions', true],
			exact: false,
		});
		void queryClient.invalidateQueries({
			queryKey: ['medical-record', resolvedPatientId],
		});
	}, [isOpen, queryClient, resolvedPatientId]);

	useEffect(() => {
		if (!isOpen || !medicalRecord?.id) return;

		void queryClient.invalidateQueries({
			queryKey: ['medical-record-sessions', medicalRecord.id],
			exact: false,
		});
	}, [isOpen, medicalRecord?.id, queryClient]);

	const {
		data: sessionsPageData,
		isLoading: isSessionsLoading,
		isFetching: isSessionsFetching,
	} = useGetMedicalRecordSessions(
		medicalRecord?.id ?? '',
		{ page: sessionsPage, limit: MEDICAL_RECORD_SESSIONS_PAGE_SIZE },
		{ enabled: isOpen && !!medicalRecord?.id },
	);

	useEffect(() => {
		const pageData = sessionsPageData?.data;
		if (!pageData) return;

		setAccumulatedSessions((prev) => {
			if (sessionsPage === 1) {
				return pageData;
			}
			const existingIds = new Set(prev.map((s) => s.id));
			const newItems = pageData.filter((s) => !existingIds.has(s.id));
			if (newItems.length === 0) return prev;
			return [...prev, ...newItems];
		});
	}, [sessionsPageData?.data, sessionsPage]);

	const platformSessionsParams = useMemo(
		() => ({
			isMe: true as const,
			patient_id: resolvedPatientId ?? undefined,
			page: 1,
			limit: 50,
		}),
		[resolvedPatientId],
	);

	const {
		data: platformSessionsResponse,
		isLoading: isPlatformSessionsLoading,
		isFetching: isPlatformSessionsFetching,
	} = useGetSessions(platformSessionsParams, {
		enabled: isOpen && !!resolvedPatientId,
	});

	const {
		data: selectedSessionDetail,
		isLoading: isDetailLoading,
		isError: isDetailError,
	} = useGetMedicalRecordSession(
		medicalRecord?.id ?? '',
		selectedSessionId ?? '',
		{
			enabled:
				isOpen &&
				rightView === 'session-detail' &&
				!!medicalRecord?.id &&
				!!selectedSessionId,
			pollMode: 'post-process',
		},
	);

	useEffect(() => {
		if (!selectedSessionDetail || !medicalRecord?.id) return;
		setAccumulatedSessions((prev) =>
			mergeMedicalRecordSessionIntoList(prev, selectedSessionDetail),
		);
	}, [selectedSessionDetail, medicalRecord?.id]);

	const platformSessionByRecordId = useMemo(
		() =>
			buildPlatformSessionByRecordId(
				accumulatedSessions,
				platformSessionsResponse?.data,
			),
		[accumulatedSessions, platformSessionsResponse?.data],
	);

	const allSessions = useMemo(
		() =>
			buildProntuarioSortedSessionsList({
				recordSessions: accumulatedSessions,
				platformSessions: platformSessionsResponse?.data,
				currentMedicalRecordSessionId:
					session?.medical_record_session?.id,
				currentShortSummary:
					session?.medical_record_session?.short_summary,
			}),
		[
			accumulatedSessions,
			platformSessionsResponse?.data,
			session?.medical_record_session?.id,
			session?.medical_record_session?.short_summary,
		],
	);

	const hasMoreSessions =
		(sessionsPageData?.pagination.total ?? 0) > accumulatedSessions.length;

	const isLoadingMoreSessions = isSessionsFetching && sessionsPage > 1;

	const isSessionsListLoading =
		sessionsPage === 1 &&
		((!!resolvedPatientId && isMedicalRecordLoading) ||
			(!!medicalRecord?.id &&
				(isSessionsLoading || isSessionsFetching)) ||
			(isOpen &&
				!!resolvedPatientId &&
				(isPlatformSessionsLoading || isPlatformSessionsFetching)));

	const selectedSessionNumber = selectedSessionDetail?.session_number ?? null;

	const selectedSessionDateLabel = useMemo(() => {
		if (!selectedSessionDetail) return null;
		const platformSession = platformSessionsResponse?.data?.find(
			(s) => s.id === selectedSessionDetail.session_id,
		);
		return platformSession
			? formatProntuarioSessionDate(platformSession.selected_date)
			: null;
	}, [selectedSessionDetail, platformSessionsResponse?.data]);

	const handleOpenSession = (sessionId: string) => {
		setSelectedSessionId(sessionId);
		setRightView('session-detail');
	};

	const patientContext = useMemo(
		() => buildProntuarioPatientContext(medicalRecord, patient),
		[medicalRecord, patient],
	);

	const patientHeader = useMemo(
		() =>
			resolveProntuarioPatientHeader(
				session,
				patient,
				null,
				patientContext,
				accumulatedSessions,
			),
		[accumulatedSessions, patient, patientContext, session],
	);

	const isPatientProfileLoading = isPatientLoading;
	const isPatientProfileError = isPatientError || !patient;

	const profileHeroMeta = useMemo(
		() => resolveProntuarioProfileHeroMeta(patient, null, session),
		[patient, session],
	);

	const handleSelectLeftSection = (section: LeftPanelSection) => {
		setActiveLeftSection((current) =>
			current === section ? null : section,
		);
	};

	const handlePrintSession = useCallback(async () => {
		if (!sessionEditorState?.getPrintFields || isPrintSaving) {
			if (!sessionEditorState?.getPrintFields) {
				toast.error('Não foi possível preparar a impressão.');
			}
			return;
		}

		setIsPrintSaving(true);
		try {
			await sessionEditorState.saveAsync();

			const printed = printProntuarioSession({
				patient: {
					fullName:
						patient?.full_name?.trim() || patientHeader.fullName,
					cpf: profileHeroMeta.cpf,
					rg: patient?.rg,
					birthDate: patient?.birth_date
						? formatDateDisplay(patient.birth_date)
						: null,
					gender: profileHeroMeta.gender,
					maritalStatus: patient?.marital_status
						? PATIENT_MARITAL_STATUS_LABELS[patient.marital_status]
						: null,
					educationLevel: patient?.education_level
						? PATIENT_EDUCATION_LEVEL_LABELS[
								patient.education_level
						  ]
						: null,
					profession: patient?.profession,
					phone: patient?.phone,
					email: patient?.email,
					emergencyContact: patient?.emergency_contact,
				},
				session: {
					number: selectedSessionNumber,
					dateLabel: selectedSessionDateLabel,
				},
				fields: sessionEditorState.getPrintFields(),
			});

			if (!printed) {
				toast.error(
					'Não foi possível preparar a impressão. Tente novamente.',
				);
			}
		} catch (error) {
			toast.error(
				getErrorMessage(
					error,
					'Erro ao salvar a sessão. Tente novamente.',
				),
			);
		} finally {
			setIsPrintSaving(false);
		}
	}, [
		isPrintSaving,
		patient,
		patientHeader.fullName,
		profileHeroMeta.cpf,
		profileHeroMeta.gender,
		selectedSessionDateLabel,
		selectedSessionNumber,
		sessionEditorState,
	]);

	useEffect(() => {
		if (!isOpen) return;
		const originalOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = originalOverflow;
		};
	}, [isOpen]);

	if (!isOpen) return null;

	return createPortal(
		<div className="fixed inset-0 z-[10020] flex items-center justify-center p-4">
			<div
				className="fixed inset-0 bg-black/50"
				onClick={() => requestExit('close-modal')}
				aria-hidden
			/>

			<div className="relative z-10 flex h-[88vh] w-[80vw] max-w-[80vw] flex-col overflow-hidden scroll-smooth rounded-2xl bg-white shadow-2xl">
				{/* Header */}
				<div className="flex shrink-0 items-center justify-between border-b border-zinc-200 px-6 py-4">
					<h2 className="text-[17px] font-semibold text-zinc-950">
						Prontuário
					</h2>
					<button
						type="button"
						onClick={() => requestExit('close-modal')}
						className="flex size-9 items-center justify-center rounded-full bg-zinc-100 text-zinc-600 transition-colors hover:bg-zinc-200 hover:text-zinc-900"
						aria-label="Fechar"
					>
						<X size={18} />
					</button>
				</div>

				{/* Columns */}
				<div className="flex min-h-0 flex-1">
					{/* Coluna esquerda */}
					<div className="flex min-h-0 w-[42%] shrink-0 flex-col overflow-hidden border-r border-zinc-200 bg-white">
						<div className="shrink-0 border-b border-zinc-200 px-5 py-5">
							{isPatientProfileLoading ? (
								<p className="py-2 text-center text-sm text-zinc-400">
									Carregando informações do paciente...
								</p>
							) : isPatientProfileError ? (
								<p className="py-2 text-center text-sm text-zinc-500">
									Não foi possível carregar as informações do
									participante.
								</p>
							) : (
								<SessionCallProntuarioPatientProfileHero
									header={patientHeader}
									cpf={profileHeroMeta.cpf}
									gender={profileHeroMeta.gender}
									variant="light"
								/>
							)}
						</div>

						<div className="flex min-h-0 flex-1 flex-col overflow-hidden">
							<ProntuarioProfileAccordionBlock
								label="Dados"
								variant="light"
								isActive={activeLeftSection === 'data'}
								onClick={() => handleSelectLeftSection('data')}
								contentMaxHeightClass={
									LEFT_PANEL_CONTENT_MAX_HEIGHT_CLASS
								}
								contentScrollClass={
									PRONTUARIO_MODAL_SCROLL_CLASS
								}
							>
								<SessionCallProntuarioPatientDetailView
									header={patientHeader}
									session={session}
									patient={patient}
									isLoading={isPatientProfileLoading}
									isError={isPatientProfileError}
									variant="light"
									includeClinicalSections={false}
									includeProfileHero={false}
								/>
							</ProntuarioProfileAccordionBlock>

							<ProntuarioProfileAccordionBlock
								label="Histórico clínico"
								variant="light"
								isActive={
									activeLeftSection === 'clinical-history'
								}
								onClick={() =>
									handleSelectLeftSection('clinical-history')
								}
								contentMaxHeightClass={
									LEFT_PANEL_CONTENT_MAX_HEIGHT_CLASS
								}
								contentScrollClass={
									PRONTUARIO_MODAL_SCROLL_CLASS
								}
							>
								<SessionCallProntuarioHistoricoTab
									variant="profile"
									profileTheme="light"
									record={patientContext.clinicalHistory}
								/>
							</ProntuarioProfileAccordionBlock>

							<ProntuarioProfileAccordionBlock
								label="Plano terapêutico"
								variant="light"
								isActive={
									activeLeftSection === 'therapeutic-plan'
								}
								onClick={() =>
									handleSelectLeftSection('therapeutic-plan')
								}
								contentMaxHeightClass={
									LEFT_PANEL_CONTENT_MAX_HEIGHT_CLASS
								}
								contentScrollClass={
									PRONTUARIO_MODAL_SCROLL_CLASS
								}
							>
								<SessionCallProntuarioPlanoTerapeuticoTab
									variant="profile"
									profileTheme="light"
									plan={patientContext.therapeuticPlan}
								/>
							</ProntuarioProfileAccordionBlock>
						</div>
					</div>

					{/* Coluna direita — sessões */}
					<div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-zinc-100/60">
						<div
							className={cn(
								'flex min-h-0 w-[200%] flex-1 shrink-0',
								RIGHT_PANEL_SLIDE_TRANSITION_CLASS,
								rightView === 'session-detail'
									? '-translate-x-1/2'
									: 'translate-x-0',
							)}
						>
							<div className="flex min-h-0 w-1/2 flex-col">
								<SessionsListHeader
									patientFullName={patientHeader.fullName}
								/>
								<div
									className={cn(
										PRONTUARIO_MODAL_SCROLL_CLASS,
										'min-h-0 flex-1 px-5 py-5',
									)}
								>
									<ProntuarioSessionsList
										variant="light"
										sessions={allSessions}
										platformSessionByRecordId={
											platformSessionByRecordId
										}
										isLoading={isSessionsListLoading}
										hasMorePastSessions={hasMoreSessions}
										isLoadingMorePastSessions={
											isLoadingMoreSessions
										}
										onLoadMorePastSessions={() => {
											if (
												!hasMoreSessions ||
												isLoadingMoreSessions
											)
												return;
											setSessionsPage((p) => p + 1);
										}}
										onOpenSession={handleOpenSession}
									/>
								</div>
							</div>

							<div className="flex min-h-0 w-1/2 flex-col">
								<div
									className={cn(
										RIGHT_PANEL_HEADER_CLASS,
										'items-center justify-between gap-3',
									)}
								>
									<button
										type="button"
										onClick={() =>
											requestExit('back-to-sessions')
										}
										className="flex items-center gap-1 text-sm font-medium text-zinc-600 hover:text-zinc-900"
									>
										<ChevronLeft size={16} />
										Sessões
									</button>
									<div className="flex shrink-0 items-center gap-2">
										<button
											type="button"
											onClick={() =>
												void handlePrintSession()
											}
											disabled={
												!sessionEditorState?.getPrintFields ||
												isPrintSaving ||
												sessionEditorState?.isPending
											}
											className={cn(
												'flex shrink-0 items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-semibold text-blue-700 transition-colors hover:bg-blue-100',
												(!sessionEditorState?.getPrintFields ||
													isPrintSaving ||
													sessionEditorState?.isPending) &&
													'cursor-not-allowed opacity-60',
											)}
										>
											<Printer
												className="size-4"
												strokeWidth={2.25}
												aria-hidden
											/>
											Imprimir
										</button>
										<button
											type="button"
											onClick={() =>
												sessionEditorState?.save()
											}
											disabled={
												!sessionEditorState?.hasChanges ||
												sessionEditorState?.isPending
											}
											className={cn(
												'flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors',
												sessionEditorState?.hasChanges
													? 'border-zinc-900 bg-zinc-900 text-white hover:bg-zinc-800'
													: 'border-zinc-200 bg-white text-zinc-400',
												(!sessionEditorState?.hasChanges ||
													sessionEditorState?.isPending) &&
													'cursor-not-allowed opacity-60',
											)}
										>
											<Save
												className="size-4"
												strokeWidth={2.25}
												aria-hidden
											/>
											Salvar
										</button>
									</div>
								</div>

								<div
									className={cn(
										PRONTUARIO_MODAL_SCROLL_CLASS,
										'min-h-0 flex-1 px-5 py-5',
									)}
								>
									{selectedSessionId ? (
										<ProntuarioEditableSessionDetailView
											key={`${selectedSessionId}-${sessionEditorRemountKey}`}
											recordId={medicalRecord?.id ?? ''}
											sessionId={selectedSessionId}
											sessionNumber={
												selectedSessionNumber
											}
											dateLabel={selectedSessionDateLabel}
											record={
												selectedSessionDetail ?? null
											}
											isLoading={isDetailLoading}
											isError={isDetailError}
											variant="light"
											onEditorStateChange={
												handleSessionEditorStateChange
											}
										/>
									) : null}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<ProntuarioUnsavedChangesModal
				isOpen={!!pendingExit}
				isSaving={isExitSaving}
				onSaveAndExit={() => void handleSaveAndExit()}
				onExitWithoutSaving={handleExitWithoutSaving}
				onCancel={() => setPendingExit(null)}
			/>
		</div>,
		document.body,
	);
};

export { ProfessionalProntuarioModal };
